import { useState, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const ImageUpload = ({ onImageUploaded, existingImages = [] }) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(existingImages);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error("Only image files are allowed");
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be under 5MB");
        continue;
      }

      setUploading(true);
      try {
        const base64 = await fileToBase64(file);
        const response = await axios.post(
          `${API}/upload/image`,
          { image: base64, filename: file.name.replace(/\.[^/.]+$/, "") },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const url = response.data.url;
        if (response.data.provider === "mock") {
          toast.info("Image logged (Cloudinary not configured). Using placeholder URL.");
          // Use original file as a local preview
          const previewUrl = URL.createObjectURL(file);
          const newImages = [...images, previewUrl];
          setImages(newImages);
          onImageUploaded?.(newImages);
        } else {
          const newImages = [...images, url];
          setImages(newImages);
          onImageUploaded?.(newImages);
          toast.success("Image uploaded!");
        }
      } catch (error) {
        toast.error("Failed to upload image");
      } finally {
        setUploading(false);
      }
    }

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onImageUploaded?.(newImages);
  };

  return (
    <div data-testid="image-upload">
      <div className="flex flex-wrap gap-3 mb-3">
        {images.map((img, index) => (
          <div key={index} className="relative w-20 h-20 bg-[#EFECE5] overflow-hidden border border-[#EAE6DF]">
            <img src={img} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeImage(index)}
              className="absolute top-0.5 right-0.5 w-5 h-5 bg-[#9E4747] text-white rounded-full flex items-center justify-center"
            >
              <X size={12} />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-20 h-20 border-2 border-dashed border-[#EAE6DF] hover:border-[#20C997] flex flex-col items-center justify-center text-[#8E8F8E] hover:text-[#20C997] transition-colors"
          data-testid="upload-btn"
        >
          {uploading ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <>
              <Upload size={18} />
              <span className="text-xs mt-1">Upload</span>
            </>
          )}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        data-testid="file-input"
      />

      <p className="text-xs text-[#8E8F8E]">
        Upload product images (max 5MB each). {images.length > 0 ? `${images.length} image(s) added.` : ""}
      </p>
    </div>
  );
};

export default ImageUpload;
